import React, { Fragment, useState, useMemo, useImperativeHandle, useEffect, useRef } from "react";
import { Table, Form, Button } from 'react-bootstrap';

const CATEGORY_MAP = {
  142: 'Carpet',
  143: 'Sheet Vinyl',
  168: 'Vinyl Plank',
  171: 'Misc Sundries',
  172: 'Wall Base',
  173: 'Misc Sundries',
};

// Defined order for category display (171 and 173 both map to Misc Sundries, use 171 as sort anchor)
const CATEGORY_ORDER = [142, 143, 168, 172, 171, 173];

function groupByCategory(itemizedEstimate) {
  const groups = {};

  const addItems = (items, type) => {
    (items || []).forEach(item => {
      const catId = item.productStructureId;
      const catName = CATEGORY_MAP[catId] || 'Other';
      const sortKey = CATEGORY_ORDER.indexOf(catId) !== -1 ? CATEGORY_ORDER.indexOf(catId) : 999;
      if (!groups[catName]) {
        groups[catName] = { masterProduct: [], material: [], labor: [], sortKey };
      }
      groups[catName][type].push(item);
    });
  };

  addItems(itemizedEstimate.masterProduct, 'masterProduct');
  addItems(itemizedEstimate.material, 'material');
  addItems(itemizedEstimate.labor, 'labor');

  return Object.entries(groups).sort((a, b) => a[1].sortKey - b[1].sortKey);
}

const EstimatesTable = React.forwardRef(({
  estimatesData, isLoading, customerInfo, printMode, printVariantId, onHasChanges
}, ref) => {

  const hasEstimatesData = Array.isArray(estimatesData) && estimatesData.length > 0;
  const shouldShowLoader = isLoading || !hasEstimatesData;
  const [rowEdits, setRowEdits] = useState({});
  const [bulkDiscounts, setBulkDiscounts] = useState({});
  const [showSundries, setShowSundries] = useState(false);
  const [showLabor, setShowLabor] = useState(false);
  // discounts[i] stores the raw input string for row i (% or $ value)
  const [discounts, setDiscounts] = useState({});

  const initializedRef = useRef(false);
  const initialStateRef = useRef(null);

  // function to calculate price using formula
  const calculatePriceFromGPM = (gpm, currentCost) => {
    const result = (currentCost * 100) / (100 - gpm);
    return isFinite(result) ? parseFloat(result.toFixed(2)) : 0;
  };

  useEffect(() => {
    if (!hasEstimatesData || initializedRef.current) return;
    initializedRef.current = true;

    const initialDiscounts = {};
    const initialBulkDiscounts = {};
    const initialRowEdits = {};

    const TYPE_PREFIX = { masterProduct: 'prod', material: 'mat', labor: 'lab' };
    estimatesData.forEach((floorPlan, i) => {
      const ed = floorPlan.estimateDiscount;
      if (!ed) return;

      // Prefill summary-row discount input and bulk discount
      initialDiscounts[i] = ed.discount;
      initialBulkDiscounts[i] = ed.discount;

      if(floorPlan?.miscellaneousitems?.sundries?.displayInEstimateSummary){
        setShowSundries(true);
      }
      if(floorPlan?.miscellaneousitems?.labor?.displayInEstimateSummary){
        setShowLabor(true);
      }
      const categories = groupByCategory(floorPlan.itemizedEstimate);

      (ed.lineItems || []).forEach(lineItem => {
        // Handle miscellaneous types before the standard category lookup
        if (lineItem.type === 'sundry') {
          const sundriesItems = floorPlan.miscellaneousitems?.sundries?.items || [];
          const matchIndex = sundriesItems.findIndex(
            item => (item.materialId || 0) === (lineItem.materialId || 0) &&
                    (item.productId  || 0) === (lineItem.productId  || 0) &&
                    (item.kitId      || 0) === (lineItem.kitId      || 0)
          );
          if (matchIndex === -1) return;
          initialRowEdits[`${i}_sundry_sund_${matchIndex}`] = {
            price: lineItem.price,
            gpm: lineItem.gpm,
            discountPct: lineItem.discount,
          };
          return;
        }

        if (lineItem.type === 'miscLabor') {
          const laborItems = floorPlan.miscellaneousitems?.labor?.items || [];
          const matchIndex = laborItems.findIndex(
            item => (item.laborItemId || 0) === (lineItem.laborItemId || 0) &&
                    (item.kitId       || 0) === (lineItem.kitId       || 0)
          );
          if (matchIndex === -1) return;
          initialRowEdits[`${i}_misclabor_mlab_${matchIndex}`] = {
            price: lineItem.price,
            gpm: lineItem.gpm,
            discountPct: lineItem.discount,
          };
          return;
        }

        const catName = CATEGORY_MAP[lineItem.productStructureId] || 'Other';
        const prefix = TYPE_PREFIX[lineItem.type];
        if (!prefix) return;

        const catGroup = categories.find(([name]) => name === catName);
        if (!catGroup) return;

        const typeItems = catGroup[1][lineItem.type];
        if (!typeItems) return;

        let matchIndex = -1;
        if (lineItem.type === 'masterProduct') {
          matchIndex = typeItems.findIndex(
            item => item.productId === lineItem.productId &&
                    item.productStructureId === lineItem.productStructureId
          );
        } else if (lineItem.type === 'material') {
          matchIndex = typeItems.findIndex(
            item => item.materialId === lineItem.materialId &&
                    item.kitId === lineItem.kitId
          );
        } else if (lineItem.type === 'labor') {
          matchIndex = typeItems.findIndex(
            item => item.laborItemId === lineItem.laborItemId &&
                    item.kitId === lineItem.kitId
          );
        }

        if (matchIndex === -1) return;

        const key = `${i}_${catName}_${prefix}_${matchIndex}`;
        initialRowEdits[key] = {
          price: lineItem.price,
          gpm: lineItem.gpm,
          discountPct: lineItem.discount,
        };
      });
    });

    // Build full snapshot including computed defaults for rows with no saved lineItem
    const fullInitialRowEdits = { ...initialRowEdits };
    estimatesData.forEach((floorPlan, i) => {
      groupByCategory(floorPlan.itemizedEstimate).forEach(([categoryName, items]) => {
        [['masterProduct', 'prod'], ['material', 'mat'], ['labor', 'lab']].forEach(([type, prefix]) => {
          items[type].forEach((line, k) => {
            const key = `${i}_${categoryName}_${prefix}_${k}`;
            if (!fullInitialRowEdits[key]) {
              fullInitialRowEdits[key] = {
                price: calculatePriceFromGPM(line.gpm, line.cost),
                gpm: line.gpm,
                discountPct: 0,
              };
            }
          });
        });
      });

      // Initialize miscellaneousitems (sundries + labor)
      (floorPlan.miscellaneousitems?.sundries?.items || []).forEach((item, k) => {
        const key = `${i}_sundry_sund_${k}`;
        if (!fullInitialRowEdits[key]) {
          fullInitialRowEdits[key] = { price: item.price || 0, gpm: item.gpm || 0, discountPct: 0 };
        }
      });
      (floorPlan.miscellaneousitems?.labor?.items || []).forEach((item, k) => {
        const key = `${i}_misclabor_mlab_${k}`;
        if (!fullInitialRowEdits[key]) {
          fullInitialRowEdits[key] = { price: item.price || 0, gpm: item.gpm || 0, discountPct: 0 };
        }
      });
    });

    initialStateRef.current = {
      discounts: { ...initialDiscounts },
      rowEdits: fullInitialRowEdits,
    };

    if (Object.keys(initialDiscounts).length > 0)    setDiscounts(initialDiscounts);
    if (Object.keys(initialBulkDiscounts).length > 0) setBulkDiscounts(initialBulkDiscounts);
    if (Object.keys(initialRowEdits).length > 0)      setRowEdits(initialRowEdits);
  }, [estimatesData]);

  // function to format a number with commas every 3 digits
  const handleFormat = (number) => {
    const n = parseFloat(number) || 0;
    return n.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  const showBadge = (name) => {
    return (customerInfo?.viableSeamsFloorPlans && customerInfo?.viableSeamsFloorPlans.length > 0 && customerInfo?.viableSeamsFloorPlans.includes(name)) ?
      <span style={{ color: "red", margin: "0", padding: "0", "fontWeight":"bold" }}>{name} * </span> : name
  }

  const handleSummarizedDiscountChange = (floorPlanIdx, val, floorPlan) => {
    setDiscounts(prev => ({ ...prev, [floorPlanIdx]: val }));

    let pct = 0;
    const str = String(val ?? '').trim();
    if (str !== '') {
      const isDollar = str.includes('$');
      const num = parseFloat(str.replace(/[$%]/g, ''));
      if (!isNaN(num) && num >= 0) {
        if (isDollar) {
          const rowTotal = floorPlan.summarizedItem.total.linePrice + floorPlan.summarizedItem.total.tax;
          pct = rowTotal > 0 ? Math.min(100, (num / rowTotal) * 100) : 0;
        } else {
          pct = Math.min(100, num);
        }
      }
    }
    pct = Math.max(0, pct);
    setDiscounts(prev => ({ ...prev, [floorPlanIdx]: pct }));
    setBulkDiscounts(prev => ({ ...prev, [floorPlanIdx]: pct }));

    const updates = {};
    groupByCategory(floorPlan.itemizedEstimate).forEach(([categoryName, items]) => {
      [['masterProduct', 'prod'], ['material', 'mat'], ['labor', 'lab']].forEach(([type, prefix]) => {
        items[type].forEach((line, k) => {
          const key = `${floorPlanIdx}_${categoryName}_${prefix}_${k}`;
          updates[key] = { ...rowEdits[key], discountPct: pct, gpm: line.gpm };
        });
      });
    });
    (floorPlan.miscellaneousitems?.sundries?.items || []).forEach((item, k) => {
      const key = `${floorPlanIdx}_sundry_sund_${k}`;
      updates[key] = { ...rowEdits[key], discountPct: pct, gpm: item.gpm };
    });
    (floorPlan.miscellaneousitems?.labor?.items || []).forEach((item, k) => {
      const key = `${floorPlanIdx}_misclabor_mlab_${k}`;
      updates[key] = { ...rowEdits[key], discountPct: pct, gpm: item.gpm };
    });
    setRowEdits(prev => ({ ...prev, ...updates }));
  };

  // Returns { discountedTotal, label } given the row total and raw discount input.
  // Entering "10%" or "10" treats it as percentage; entering "$50" or "50$" treats it as a dollar amount.
  const calcDiscounted = (total, rawVal) => {
    if (rawVal === undefined || rawVal === null || rawVal === "") {
      return { discountedTotal: null, label: "" };
    }
    const str = String(rawVal).trim();
    const isDollar = str.includes("$");
    const num = parseFloat(str.replace(/[$%]/g, ""));
    if (isNaN(num) || num < 0) return { discountedTotal: null, label: "" };

    let discountedTotal;
    let label;
    if (isDollar) {
      discountedTotal = Math.max(0, total - num);
      label = `-$${handleFormat(num)}`;
    } else {
      const pct = Math.min(num, 100);
      discountedTotal = total * (1 - pct / 100);
      label = `-${handleFormat(pct)}%`;
    }
    return { discountedTotal, label };
  };

  // function to calculate GPM using formula
  const calculateGPM = (price, currentCost) => {
    // price = parseFloat(price) || 0
    let result = ((price - currentCost) / price) * 100;
    result = Math.round(result * 100) / 100
    return isFinite(result) ? result : 0;
  };

  const calculateDiscount = (price, discountPct) => {
    const p = parseFloat(price) || 0;
    const pct = Math.min(100, Math.max(0, parseFloat(discountPct) || 0));
    const discountAmount = p * (pct / 100);
    let discountedPrice = Math.max(0, p - discountAmount);
    discountedPrice = Math.round((discountedPrice + Number.EPSILON) * 100) / 100;
    return { discountAmount, discountedPrice};
  };

  const getRowState = (key, line) => {
    const price = line.price || 0;
    // const cost = line.cost || 0;
    const defaults = { price, gpm: line.gpm, discountPct: 0 };
    // const defaults = { price:calculatePriceFromGPM(line.gpm, line.cost), gpm: line.gpm, discountPct: 0 };
    return rowEdits[key] ? { ...defaults, ...rowEdits[key] } : defaults;
  };

  const handlePriceChange = (key, val, cost) => {
    const price = parseFloat(val) || 0;
    setRowEdits(prev => ({ ...prev, [key]: { ...prev[key], price, gpm: calculateGPM(price, cost) } }));
  };

  const handleGpmCalChange = (key, val, cost) => {
    const gpm = parseFloat(val) || 0;
    setRowEdits(prev => ({ ...prev, [key]: { ...prev[key], gpm, price: calculatePriceFromGPM(gpm, cost) } }));
  };

  const handleDiscountPctChange = (key, val) => {
    const discountPct = Math.min(100, Math.max(0, parseFloat(val) || 0));
    setRowEdits(prev => ({ ...prev, [key]: { ...prev[key], discountPct } }));
  };

  const handleBulkApply = (floorPlanIdx, floorPlan) => {
    const pct = Math.min(100, Math.max(0, parseFloat(bulkDiscounts[floorPlanIdx]) || 0));
    const updates = {};
    groupByCategory(floorPlan.itemizedEstimate).forEach(([categoryName, items]) => {
      [['masterProduct', 'prod'], ['material', 'mat'], ['labor', 'lab']].forEach(([type, prefix]) => {
        items[type].forEach((line, k) => {
          const key = `${floorPlanIdx}_${categoryName}_${prefix}_${k}`;
          updates[key] = { ...rowEdits[key], discountPct: pct, gpm: line.gpm };
        });
      });
    });
    (floorPlan.miscellaneousitems?.sundries?.items || []).forEach((item, k) => {
      const key = `${floorPlanIdx}_sundry_sund_${k}`;
      updates[key] = { ...rowEdits[key], discountPct: pct, gpm: item.gpm };
    });
    (floorPlan.miscellaneousitems?.labor?.items || []).forEach((item, k) => {
      const key = `${floorPlanIdx}_misclabor_mlab_${k}`;
      updates[key] = { ...rowEdits[key], discountPct: pct, gpm: item.gpm };
    });
    setRowEdits(prev => ({ ...prev, ...updates }));
  };

  const  isSalesperson = () => {
    return !JSON.parse(localStorage.getItem('showSeamCutPlan'))
  }

  const getCategoryTypeTotals = (floorPlanIdx, categoryName, lines, prefix) => {
    let totalCost = 0, totalOriginalSell = 0, totalSell = 0;
    lines.forEach((line, k) => {
      const key = `${floorPlanIdx}_${categoryName}_${prefix}_${k}`;
      const rs = getRowState(key, line);
      let { discountedPrice } = calculateDiscount(rs.price, rs.discountPct);
      // discountedPrice = Math.round((discountedPrice + Number.EPSILON) * 100) / 100;
      const qty = parseFloat(line.quantity) || 1;

      totalCost         = Math.round((totalCost         + (parseFloat(line.cost) || 0) * qty) * 100) / 100;
      totalOriginalSell = Math.round((totalOriginalSell + parseFloat(rs.price) * qty)         * 100) / 100;
      totalSell         = Math.round((totalSell         + parseFloat(discountedPrice) * qty)  * 100) / 100;
    });
    return {
      totalCost,
      totalOriginalSell,
      originalGPM: calculateGPM(totalOriginalSell, totalCost),
      totalSell,
      gpm: calculateGPM(totalSell, totalCost),
    };
  };

  const renderCategorySubtotal = (label, totals, bgColor, hideDiscountedSell = false) => (
    <tr className="print-hide" style={{ background: bgColor, fontWeight: 'bold', fontSize: '0.9rem' }}>
      <td colSpan={isSalesperson() ? 1 : 2}>{label}</td>
      {!isSalesperson() && <td>${handleFormat(totals.totalCost)}</td>}
      <td>${handleFormat(totals.totalOriginalSell)}</td>
      <td className="print-hide">{handleFormat(totals.originalGPM)}%</td>
      <td></td>
      <td className="print-hide"></td>
      <td className={hideDiscountedSell ? 'print-hide' : ''}>${handleFormat(totals.totalSell)}</td>
      <td className="print-hide">{handleFormat(totals.gpm)}%</td>
    </tr>
  );

  const renderItemRow = (line, key, badge, hideDiscountedPrice = false) => {
    const rs = getRowState(key, line);
    const qty = line.quantity || 1;
    const lineTotal = rs.price * qty;
    const discountedLineTotal = lineTotal * (1 - rs.discountPct / 100);
    let discountedPricePerUnit = qty > 0 ? discountedLineTotal / qty : 0;
    discountedPricePerUnit = Math.round((discountedPricePerUnit + Number.EPSILON) * 100) / 100;
    // discountedPricePerUnit = Math.round(discountedPricePerUnit * 100) / 100 // Math.floor(discountedPricePerUnit * 100) / 100
    const discountGPM = calculateGPM(discountedPricePerUnit, line.cost || 0);
    return (
      <tr key={key}>
        <td>{line.name}{' '}{badge}</td>
        {!isSalesperson() && <td>{qty}</td>}
        {!isSalesperson() && <td className="print-hide">${handleFormat(line.cost || 0)}</td>}
        <td >
          <Form.Control type="number" size="sm" min={0}
            value={rs.price}
            onChange={e => handlePriceChange(key, e.target.value, line.cost || 0)}
            style={{ width: '80px' }} />
        </td>
        <td className="print-hide">
          <Form.Control type="number" size="sm" max={99.99}
            value={parseFloat((rs.gpm || 0).toFixed(2))}
            onChange={e => handleGpmCalChange(key, e.target.value, line.cost || 0)}
            style={{ width: '70px' }} />
        </td>
        <td>${handleFormat(lineTotal)}</td>
        <td className="print-hide">
          <Form.Control type="number" size="sm" min={0} max={100}
            value={rs.discountPct}
            onChange={e => handleDiscountPctChange(key, e.target.value)}
            style={{ width: '70px' }} />
        </td>
        <td className={hideDiscountedPrice ? 'print-hide' : ''}>${handleFormat(discountedPricePerUnit)}</td>
        <td className="print-hide" style={{ fontWeight: rs.discountPct > 0 ? '600' : 'normal' }}>
          {handleFormat(discountGPM)}%
        </td>
      </tr>
    );
  };

  // Aggregates cost and sell price (original + discounted) for one item type across all categories of a layout
  const getLayoutTotals = (floorPlanIdx, floorPlan) => {
    const categories = groupByCategory(floorPlan.itemizedEstimate);
    const sumType = (type, prefix) => {
      let totalCost = 0, totalOriginalSell = 0, totalSell = 0;
      categories.forEach(([categoryName, items]) => {
        items[type].forEach((line, k) => {
          const key = `${floorPlanIdx}_${categoryName}_${prefix}_${k}`;
          const rs = getRowState(key, line);
          const qty = parseFloat(line.quantity) || 1;
          const { discountedPrice } = calculateDiscount(rs.price, rs.discountPct);
          totalCost         = Math.round((totalCost         + (parseFloat(line.cost) || 0) * qty) * 100) / 100;
          totalOriginalSell = Math.round((totalOriginalSell + parseFloat(rs.price)          * qty) * 100) / 100;
          totalSell         = Math.round((totalSell         + parseFloat(discountedPrice)   * qty) * 100) / 100;
        });
      });
      return { totalCost, totalOriginalSell, totalSell };
    };

    const sumMiscItems = (items, keyPrefix) => {
      let totalCost = 0, totalOriginalSell = 0, totalSell = 0;
      (items || []).forEach((item, k) => {
        const key = `${floorPlanIdx}_${keyPrefix}_${k}`;
        const rs = getRowState(key, item);
        const qty = parseFloat(item.quantity) || 1;
        const { discountedPrice } = calculateDiscount(rs.price, rs.discountPct);
        totalCost         = Math.round((totalCost         + (parseFloat(item.cost) || 0) * qty) * 100) / 100;
        totalOriginalSell = Math.round((totalOriginalSell + parseFloat(rs.price)          * qty) * 100) / 100;
        totalSell         = Math.round((totalSell         + parseFloat(discountedPrice)   * qty) * 100) / 100;
      });
      return { totalCost, totalOriginalSell, totalSell };
    };

    const tmpMat      = sumType('material', 'mat');
    const tmpProd     = sumType('masterProduct', 'prod');
    const tmpLab      = sumType('labor', 'lab');
    const miscSundry  = sumMiscItems(floorPlan.miscellaneousitems?.sundries?.items, 'sundry_sund');
    const miscLabor   = sumMiscItems(floorPlan.miscellaneousitems?.labor?.items,    'misclabor_mlab');

    const buildTotals = (parts) => {
      const totalCost         = Math.round(parts.reduce((s, p) => s + p.totalCost,         0) * 100) / 100;
      const totalOriginalSell = Math.round(parts.reduce((s, p) => s + p.totalOriginalSell, 0) * 100) / 100;
      const totalSell         = Math.round(parts.reduce((s, p) => s + p.totalSell,         0) * 100) / 100;
      return {
        totalCost,
        totalOriginalSell,
        originalGPM: calculateGPM(totalOriginalSell, totalCost),
        totalSell,
        gpm: calculateGPM(totalSell, totalCost),
      };
    };

    return {
      material: buildTotals([tmpMat, tmpProd, miscSundry]),
      labor:    buildTotals([tmpLab, miscLabor]),
    };
  };

  // Grand total across all layouts — memoized to avoid recomputing on unrelated renders
  const grandTotals = useMemo(() => {
    if (!hasEstimatesData) return null;
    let matCost = 0, matSell = 0, labCost = 0, labSell = 0;
    estimatesData.forEach((floorPlan, i) => {
      const categories = groupByCategory(floorPlan.itemizedEstimate);
      categories.forEach(([categoryName, items]) => {
        items.material.forEach((line, k) => {
          const key = `${i}_${categoryName}_mat_${k}`;
          const rs = getRowState(key, line);
          const qty = parseFloat(line.quantity) || 1;
          let { discountedPrice } = calculateDiscount(rs.price, rs.discountPct);
          // discountedPrice = Math.round((discountedPrice + Number.EPSILON) * 100) / 100;
          matCost = Math.round((matCost + (parseFloat(line.cost) || 0) * qty) * 100) / 100;
          matSell = Math.round((matSell + parseFloat(discountedPrice) * qty) * 100) / 100;

        });
        items.labor.forEach((line, k) => {
          const key = `${i}_${categoryName}_lab_${k}`;
          const rs = getRowState(key, line);
          const qty = parseFloat(line.quantity) || 1;
          let { discountedPrice } = calculateDiscount(rs.price, rs.discountPct);
          // discountedPrice = Math.round((discountedPrice + Number.EPSILON) * 100) / 100;
          labCost = Math.round((labCost + (parseFloat(line.cost) || 0) * qty) * 100) / 100;
          labSell = Math.round((labSell + parseFloat(discountedPrice) * qty) * 100) / 100;
        });
      });
    });
    const totalCost = Math.round((matCost + labCost) * 100) / 100;
    const totalSell = Math.round((matSell + labSell) * 100) / 100;
    return {
      material: { totalCost: matCost, totalSell: matSell, gpm: calculateGPM(matSell, matCost) },
      labor:    { totalCost: labCost, totalSell: labSell, gpm: calculateGPM(labSell, labCost) },
      overall:  { totalCost, totalSell, gpm: calculateGPM(totalSell, totalCost) },
    };
  }, [estimatesData, rowEdits]);

  const buildSavePayload = () => {
    return estimatesData.map((floorPlan, i) => {
      const lineItems = [];
      groupByCategory(floorPlan.itemizedEstimate).forEach(([categoryName, items]) => {
        [
          [items.masterProduct, 'prod', 'masterProduct'],
          [items.material,      'mat',  'material'],
          [items.labor,         'lab',  'labor'],
        ].forEach(([lines, prefix, type]) => {
          lines.forEach((line, k) => {
            const key = `${i}_${categoryName}_${prefix}_${k}`;
            const rs = getRowState(key, line);
            const { discountedPrice } = calculateDiscount(rs.price, rs.discountPct);
            lineItems.push({
              // id: line.id,
              // name: line.name,
              type,
              price: rs.price,
              gpm: rs.gpm,
              discount: rs.discountPct,
              discountedPrice: Math.round(discountedPrice * 100) / 100,
              productStructureId: line.productStructureId,
              kitId: line.kitId,
              laborItemId: line?.laborItemId || 0,
              materialId: line?.materialId || 0,
              productId: line?.productId || 0,
              unitTypeId: floorPlan?.unitTypeId || 0,
              variantId: floorPlan.variantId,
            });
          });
        });
      });

      (floorPlan.miscellaneousitems?.sundries?.items || []).forEach((item, k) => {
        const key = `${i}_sundry_sund_${k}`;
        const rs = getRowState(key, item);
        const { discountedPrice } = calculateDiscount(rs.price, rs.discountPct);
        lineItems.push({
          type: 'sundry',
          price: rs.price,
          gpm: rs.gpm,
          discount: rs.discountPct,
          discountedPrice: Math.round(discountedPrice * 100) / 100,
          productStructureId: item.productStructureId || 0,
          kitId: item.kitId || 0,
          laborItemId: 0,
          materialId: item?.materialId || 0,
          productId: item?.productId || 0,
          unitTypeId: floorPlan?.unitTypeId || 0,
          variantId: floorPlan.variantId,
        });
      });

      (floorPlan.miscellaneousitems?.labor?.items || []).forEach((item, k) => {
        const key = `${i}_misclabor_mlab_${k}`;
        const rs = getRowState(key, item);
        const { discountedPrice } = calculateDiscount(rs.price, rs.discountPct);
        lineItems.push({
          type: 'miscLabor',
          price: rs.price,
          gpm: rs.gpm,
          discount: rs.discountPct,
          discountedPrice: Math.round(discountedPrice * 100) / 100,
          productStructureId: item.productStructureId || 0,
          kitId: item.kitId || 0,
          laborItemId: item?.laborItemId || 0,
          materialId: 0,
          productId: 0,
          unitTypeId: floorPlan?.unitTypeId || 0,
          variantId: floorPlan.variantId,
        });
      });

      return {
        variantId: floorPlan.variantId,
        discount: parseFloat(bulkDiscounts[i]) || 0,
        unitTypeId: floorPlan?.unitTypeId || 0,
        lineItems,
      };
    });
  };

  const hasChanges = useMemo(() => {
    if (!initialStateRef.current) return false;
    const { discounts: initDiscounts, rowEdits: initRowEdits } = initialStateRef.current;

    const parseNum = val => parseFloat(String(val ?? 0).replace(/[$%]/g, '')) || 0;

    const allDiscountKeys = new Set([
      ...Object.keys(initDiscounts).map(String),
      ...Object.keys(discounts).map(String),
    ]);
    for (const key of allDiscountKeys) {
      if (Math.abs(parseNum(initDiscounts[key]) - parseNum(discounts[key])) > 0.001) return true;
    }

    const allRowKeys = new Set([...Object.keys(initRowEdits), ...Object.keys(rowEdits)]);
    for (const key of allRowKeys) {
      const init = initRowEdits[key] || {};
      const cur  = rowEdits[key]     || init;
      if (Math.abs(parseNum(init.price)       - parseNum(cur.price))       > 0.001) return true;
      if (Math.abs(parseNum(init.gpm)         - parseNum(cur.gpm))         > 0.001) return true;
      if (Math.abs(parseNum(init.discountPct) - parseNum(cur.discountPct)) > 0.001) return true;
    }

    return false;
  }, [discounts, rowEdits]);

  useEffect(() => {
    onHasChanges?.(hasChanges);
  }, [hasChanges]);

  useImperativeHandle(ref, () => ({ buildSavePayload, hasChanges }));

  const isDiscountApplied = (val) => val !== null && val !== '' && parseFloat(val) !== 0;
  const anyHasDiscount = hasEstimatesData && estimatesData.some((_, i) => isDiscountApplied(discounts[i]));

  const floorPlanHasAnyDiscount = (fpIdx, fp) =>
    groupByCategory(fp.itemizedEstimate).some(([catName, items]) =>
      [['masterProduct', 'prod'], ['material', 'mat'], ['labor', 'lab']].some(([type, prefix]) =>
        items[type].some((line, k) => getRowState(`${fpIdx}_${catName}_${prefix}_${k}`, line).discountPct > 0)
      )
    );

  return (
    <Fragment>
      {shouldShowLoader ? (<div className="loading-container"><div className="spinner-border"></div></div>) : null}
      
      <Fragment>
        <Table data-displaytype={'summary'} responsive bordered className={(Array.isArray(printMode) ? printMode : [printMode]).includes('summary') ? '' : 'print-hide'}>
          <thead>
            <tr>
              <th>
                Floor Plan
              </th>
              <th>
                Vinyl
              </th>
              <th>
                Vinyl Plank
              </th>
              <th>
                Carpet
              </th>
              <th>
                Wall Base
              </th>
              {showSundries && <th>
                Sundries
              </th>}
              {showLabor && <th>
                Labor
              </th>}
              <th>
                Tax
              </th>
              <th>
                Total
              </th>
              <th className="print-hide">Discount <span style={{ fontWeight: "normal", fontSize: "0.78rem" }}>%</span></th>
              <th className={!anyHasDiscount && "print-hide"}>Discounted Total</th>
            </tr>
          </thead>
          <tbody>
            {estimatesData.map((k, i) => {
              const rowTotal = k.summarizedItem.total.linePrice + k.summarizedItem.total.tax;
              const { discountedTotal } = calcDiscounted(rowTotal, discounts[i]);
              const modesArr = Array.isArray(printMode) ? printMode : [printMode];
              const hasSummary = modesArr.includes('summary');
              const layoutModes = modesArr.filter(m => m !== 'summary');
              let summaryRowHidden = !hasSummary;
              if (hasSummary && layoutModes.length > 0) {
                let rowMatches = false;
                if (modesArr.includes('all-default') && k.isDefaultVariant) rowMatches = true;
                if (modesArr.includes('all-alternate') && !k.isDefaultVariant) rowMatches = true;
                if (modesArr.includes('individual') && String(k.variantId) === String(printVariantId)) rowMatches = true;
                summaryRowHidden = !rowMatches;
              }
              return (
                <tr key={i} data-isdefault={k.isDefaultVariant} data-variantid={k.variantId} className={summaryRowHidden ? 'print-hide' : ''}>
                  <td>{showBadge(k.floorPlanName)} {k.variantName && `(${k.variantName})`} {k.isDefaultVariant && (<span className="badge bg-secondary rounded-pill p-1 badge-sm">Default</span>)}</td>
                  <td>{k.summarizedItem.vinyl?.linePrice === 0.0 ? "" : "$" + handleFormat(k.summarizedItem.vinyl?.linePrice)}</td>
                  <td>${handleFormat(k.summarizedItem.vinylPlank?.linePrice)}</td>
                  <td>${handleFormat(k.summarizedItem.carpet?.linePrice)}</td>
                  <td>${handleFormat(k.summarizedItem?.wallbase.linePrice)}</td>
                  {showSundries && <td>
                    ${handleFormat(k.summarizedItem?.miscProduct?.linePrice)}
                  </td>}
                  {showLabor && <td>
                    ${handleFormat(k.summarizedItem?.miscLabor?.linePrice)}
                  </td>}
                  <td>${handleFormat(k.summarizedItem.total.tax)}</td>
                  <td>${handleFormat(rowTotal)}</td>
                  <td className="print-hide">
                    <Form.Control
                      type="number" min={0} max={100}
                      size="sm"
                      placeholder="e.g. 10%"
                      value={discounts[i] ?? 0}
                      onChange={e => handleSummarizedDiscountChange(i, e.target.value, k)}
                      style={{ width: '60%'}}
                    />
                  </td>
                  <td className={!anyHasDiscount && "print-hide"} style={{ fontWeight: discountedTotal !== null ? "600" : "normal" }}>
                    {discountedTotal !== null ? `$${handleFormat(discountedTotal)}` : `$${handleFormat(rowTotal)}`}
                  </td>
                </tr>
              );
            })}

          </tbody>
        </Table>

        {(() => {
          const modesArr = Array.isArray(printMode) ? printMode : [printMode];
          const hasSummary = modesArr.includes('summary');
          let firstVisibleSeen = false;
          return estimatesData.map((floorPlan, i) => {
          let hideInPrint = true;
          if (modesArr.includes('all-default') && floorPlan.isDefaultVariant) hideInPrint = false;
          if (modesArr.includes('all-alternate') && !floorPlan.isDefaultVariant) hideInPrint = false;
          if (modesArr.includes('individual') && String(floorPlan.variantId) === String(printVariantId)) hideInPrint = false;
          const skipPageBreak = !hasSummary && !hideInPrint && !firstVisibleSeen;
          if (!hideInPrint) firstVisibleSeen = true;
          const fpHasDiscount = floorPlanHasAnyDiscount(i, floorPlan);
          return (
          <div key={i} className={hideInPrint ? 'print-hide' : ''}>
          {!skipPageBreak && <div className="pagebreak" data-isdefault={floorPlan.isDefaultVariant} data-variantid={floorPlan.variantId}/>}
          <div>
            <img src={floorPlan.floorPlanImage} alt="Standard Logo" className="floor-plan-image" />
            <h3>Estimate for {showBadge(floorPlan.floorPlanName)} {floorPlan.variantName && `(${floorPlan.variantName})`} {floorPlan.isDefaultVariant && (<span className="badge bg-secondary rounded-pill p-1 badge-sm">Default</span>)}</h3>

            <div className="d-flex align-items-center gap-2 mb-2 no-print">
              <span style={{ fontWeight: '600' }}>Bulk Discount %:</span>
              <Form.Control
                type="number" size="sm" min={0} max={100} placeholder="0"
                style={{ width: '90px' }}
                value={bulkDiscounts[i] ?? ''}
                onChange={e => setBulkDiscounts(prev => ({ ...prev, [i]: e.target.value }))}
              />
              <Button size="sm" variant="primary" onClick={() => handleBulkApply(i, floorPlan)}>Apply</Button>
            </div>

            <Table responsive bordered>
              <thead>
                <tr>
                  <th>Line Item</th>
                  {!isSalesperson() && <th>QTY</th>}
                  {!isSalesperson() && <th className="print-hide">Cost</th>}
                  <th>Price</th>
                  <th className="print-hide">GPM %</th>
                  <th>Line Total</th>
                  <th className="print-hide">Discount %</th>
                  <th className={!fpHasDiscount ? 'print-hide' : ''}>Discounted Price</th>
                  <th className="print-hide">Discounted GPM</th>
                </tr>
              </thead>
              <tbody>
                {groupByCategory(floorPlan.itemizedEstimate).map(([categoryName, items]) => {
                  const catProd = getCategoryTypeTotals(i, categoryName, items.masterProduct, 'prod');
                  const catMat  = getCategoryTypeTotals(i, categoryName, items.material, 'mat');
                  const catLab  = getCategoryTypeTotals(i, categoryName, items.labor, 'lab');
                  const matTotalCost         = Math.round((catProd.totalCost + catMat.totalCost) * 100) / 100;
                  const matTotalOriginalSell = Math.round((catProd.totalOriginalSell + catMat.totalOriginalSell) * 100) / 100;
                  const matTotalSell         = Math.round((catProd.totalSell + catMat.totalSell) * 100) / 100;
                  const catMaterial = {
                    totalCost: matTotalCost,
                    totalOriginalSell: matTotalOriginalSell,
                    originalGPM: calculateGPM(matTotalOriginalSell, matTotalCost),
                    totalSell: matTotalSell,
                    gpm: calculateGPM(matTotalSell, matTotalCost),
                  };
                  const hasMaterial = items.masterProduct.length > 0 || items.material.length > 0;
                  const hasLabor = items.labor.length > 0;
                  return (
                    <Fragment key={categoryName}>
                      <tr>
                        <td colSpan={9} style={{ background: '#f0f0f0', fontWeight: 'bold', fontSize: '1.05rem' }}>
                          {categoryName}
                        </td>
                      </tr>
                      {items.masterProduct.map((line, k) =>
                        renderItemRow(
                          line,
                          `${i}_${categoryName}_prod_${k}`,
                          <span className="badge bg-primary rounded-pill p-1 badge-sm">Product</span>,
                          !fpHasDiscount
                        )
                      )}
                      {items.material.map((line, k) =>
                        renderItemRow(
                          line,
                          `${i}_${categoryName}_mat_${k}`,
                          line.kitId > 0
                            ? <span className="badge bg-success rounded-pill p-1 badge-sm">Kit Material</span>
                            : <span className="badge bg-secondary rounded-pill p-1 badge-sm">Misc Material</span>,
                          !fpHasDiscount
                        )
                      )}
                      {items.labor.map((line, k) =>
                        renderItemRow(
                          line,
                          `${i}_${categoryName}_lab_${k}`,
                          line.kitId > 0
                            ? <span className="badge bg-info rounded-pill p-1 badge-sm">Kit Labor</span>
                            : <span className="badge bg-secondary rounded-pill p-1 badge-sm">Misc Labor</span>,
                          !fpHasDiscount
                        )
                      )}
                      {hasMaterial && renderCategorySubtotal(`${categoryName} - Materials`, catMaterial, '#e8f5e9', !fpHasDiscount)}
                      {hasLabor    && renderCategorySubtotal(`${categoryName} - Labor`,     catLab,      '#e3f2fd', !fpHasDiscount)}
                    </Fragment>
                  );
                })}

                {/* Sundries section */}
                {(() => {
                  const sundriesItems = floorPlan.miscellaneousitems?.sundries?.items || [];
                  if (sundriesItems.length === 0) return null;
                  const sundriesTotals = getCategoryTypeTotals(i, 'sundry', sundriesItems, 'sund');
                  return (
                    <Fragment>
                      <tr>
                        <td colSpan={9} style={{ background: '#f0f0f0', fontWeight: 'bold', fontSize: '1.05rem' }}>Sundries</td>
                      </tr>
                      {sundriesItems.map((item, k) =>
                        renderItemRow(
                          item,
                          `${i}_sundry_sund_${k}`,
                          '',
                          !fpHasDiscount
                        )
                      )}
                      {renderCategorySubtotal('Sundries - Total', sundriesTotals, '#e8f5e9', !fpHasDiscount)}
                    </Fragment>
                  );
                })()}

                {/* Misc Labor section */}
                {(() => {
                  const laborItems = floorPlan.miscellaneousitems?.labor?.items || [];
                  if (laborItems.length === 0) return null;
                  const miscLaborTotals = getCategoryTypeTotals(i, 'misclabor', laborItems, 'mlab');
                  return (
                    <Fragment>
                      <tr>
                        <td colSpan={9} style={{ background: '#f0f0f0', fontWeight: 'bold', fontSize: '1.05rem' }}>Misc Labor</td>
                      </tr>
                      {laborItems.map((item, k) =>
                        renderItemRow(
                          item,
                          `${i}_misclabor_mlab_${k}`,
                          '',
                          !fpHasDiscount
                        )
                      )}
                      {renderCategorySubtotal('Misc Labor - Total', miscLaborTotals, '#e3f2fd', !fpHasDiscount)}
                    </Fragment>
                  );
                })()}
              </tbody>
              <tfoot>
                {(() => {
                  const { material, labor } = getLayoutTotals(i, floorPlan);
                  return (
                    <Fragment>
                      {renderCategorySubtotal('Materials Total', material, '#e8f5e9', !fpHasDiscount)}
                      {renderCategorySubtotal('Labor Total',     labor,    '#e3f2fd', !fpHasDiscount)}
                    </Fragment>
                  );
                })()}
              </tfoot>
            </Table>
          </div>
          </div>
          );
        });
        })()}

        {grandTotals && (
          <div className="mt-4 mb-2 pagebreak" style={{display:'none'}}>
            <h4>Grand Total Summary</h4>
            <Table bordered style={{ maxWidth: '520px' }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th>Type</th>
                  <th>Total Cost</th>
                  <th>Total Sell</th>
                  <th>GPM %</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ background: '#e8f5e9' }}>
                  <td><strong>Materials</strong></td>
                  <td>${handleFormat(grandTotals.material.totalCost)}</td>
                  <td>${handleFormat(grandTotals.material.totalSell)}</td>
                  <td>{handleFormat(grandTotals.material.gpm)}%</td>
                </tr>
                <tr style={{ background: '#e3f2fd' }}>
                  <td><strong>Labor</strong></td>
                  <td>${handleFormat(grandTotals.labor.totalCost)}</td>
                  <td>${handleFormat(grandTotals.labor.totalSell)}</td>
                  <td>{handleFormat(grandTotals.labor.gpm)}%</td>
                </tr>
                <tr style={{ background: '#fff9c4', fontWeight: 'bold' }}>
                  <td>Overall</td>
                  <td>${handleFormat(grandTotals.overall.totalCost)}</td>
                  <td>${handleFormat(grandTotals.overall.totalSell)}</td>
                  <td>{handleFormat(grandTotals.overall.gpm)}%</td>
                </tr>
              </tbody>
            </Table>
          </div>
        )}
      </Fragment>

    </Fragment>
  )
});

export default EstimatesTable
